/**
 * Domain constants + helpers for the "My Skills" page.
 *
 * Skill data comes from the API (see ./skillsApi). A skill object has the shape:
 *  - evidenceMonths : aggregate duration inferred from work-history evidence
 *  - inputMonths    : the candidate's own number (null until they enter one)
 *  - stars          : 1–4 rating, source of truth for proficiency
 *  - recency        : candidate's chosen recency band (null = use inference)
 */

export const SK_TYPE_META = {
  DOMAIN_SKILL: {
    chip: 'skill-type-chip--domain',
    label: 'Domain',
    group: 'Domain Skills',
  },
  TOOL: { chip: 'skill-type-chip--tool', label: 'Tool', group: 'Tools' },
  SOFT_SKILL: {
    chip: 'skill-type-chip--soft',
    label: 'Soft skill',
    group: 'Soft Skills',
  },
};

export const SK_PROF_LEVELS = [
  'BEGINNER',
  'INTERMEDIATE',
  'ADVANCED',
  'EXPERT',
];

// 4-star rating, one star per proficiency level: 1★ Beginner, 2★ Intermediate,
// 3★ Advanced, 4★ Expert. `stars` (1–4) is the source of truth for fill; the
// level is derived from it. SK_LEVEL_TO_STAR seeds stars from a pre-set level
// (e.g. data that already carries a proficiency).
export const SK_STAR_TO_LEVEL = [
  null,
  'BEGINNER',
  'INTERMEDIATE',
  'ADVANCED',
  'EXPERT',
]; // index 1..4
export const SK_LEVEL_TO_STAR = {
  BEGINNER: 1,
  INTERMEDIATE: 2,
  ADVANCED: 3,
  EXPERT: 4,
};
export function skStarCount(s) {
  if (s.stars != null) return s.stars;
  if (s.proficiency && SK_LEVEL_TO_STAR[s.proficiency]) {
    return SK_LEVEL_TO_STAR[s.proficiency];
  }
  return 0;
}

export const SK_RECENCY = [
  { key: 'CURRENT', label: 'Currently using' },
  { key: 'WITHIN_1Y', label: 'Within a year' },
  { key: 'ONE_TO_3Y', label: '1–3 years ago' },
  { key: 'OVER_3Y', label: '3+ years ago' },
];

// Compact-row recency: a short label + a freshness tone (green → amber → grey)
// so "how recent" reads as a colour cue, distinct from the duration value.
export const SK_RECENCY_META = {
  CURRENT: { short: 'Active now', tone: 'fresh' },
  WITHIN_1Y: { short: '<1 yr ago', tone: 'fresh' },
  ONE_TO_3Y: { short: '1–3 yrs ago', tone: 'fading' },
  OVER_3Y: { short: '3+ yrs ago', tone: 'stale' },
};

/** Below the 768px shell breakpoint — pending skills collapse to thin rows here. */
export function skIsMobile() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 767px)').matches
  );
}

export const SK_FIELD_FAIL = {
  experience: "Add how long you've used it",
  proficiency: 'Pick a proficiency level',
  recency: 'Pick how recently you used it',
};

export function skProfLabel(p) {
  if (!p) return 'Not set';
  return p.charAt(0) + p.slice(1).toLowerCase();
}

export function skMonthsHuman(m) {
  if (m == null) return '';
  if (m < 12) return `${m} mo`;
  const y = Math.floor(m / 12);
  const r = m % 12;
  return r ? `${y}y ${r}m` : `${y}${y === 1 ? ' year' : ' years'}`;
}

export function skLastUsed(s) {
  if (s.is_currently_used) return 'Currently using';
  if (!s.last_used_date) return null;
  const d = new Date(`${s.last_used_date}T00:00:00`);
  return `Last used ${d.toLocaleString('en-US', { month: 'short', year: 'numeric' })}`;
}

/** Best-guess recency band purely from extracted data (null = nothing to infer). */
export function skInferRecency(s) {
  if (s.is_currently_used) return 'CURRENT';
  if (!s.last_used_date) return null;
  const d = new Date(`${s.last_used_date}T00:00:00`);
  const now = new Date();
  const months =
    (now.getFullYear() - d.getFullYear()) * 12 +
    (now.getMonth() - d.getMonth());
  if (months < 12) return 'WITHIN_1Y';
  if (months < 36) return 'ONE_TO_3Y';
  return 'OVER_3Y';
}

/** What pill is lit: the candidate's choice if set, else our inference. */
export function skRecencyBand(s) {
  return s.recency != null ? s.recency : skInferRecency(s);
}

// ---- required-field completeness (experience, proficiency, recency) ----
// An inferred value already satisfies the field, so most cards only need proficiency.
export function skExpOK(s) {
  return (s.evidenceMonths || 0) > 0 || s.inputMonths != null;
}
export function skProfOK(s) {
  return !!s.proficiency;
}
export function skRecOK(s) {
  return skRecencyBand(s) != null;
}

export function skMissing(s) {
  const m = [];
  if (!skExpOK(s)) m.push('experience');
  if (!skProfOK(s)) m.push('proficiency');
  if (!skRecOK(s)) m.push('recency');
  return m;
}

export function skTotal(s) {
  return Math.max(s.evidenceMonths || 0, s.inputMonths || 0);
}

export function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
